import { useRef, useState } from 'react';

interface Props {
  value?: string;
  onSelect: (file: File) => void;
  onRemove: () => void;
}

export default function FeaturedImage({ value, onSelect, onRemove }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [drag, setDrag] = useState(false);

  const onInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onSelect(file);
    e.target.value = '';
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDrag(false);
    const file = e.dataTransfer.files?.[0];
    if (file && /^image\//.test(file.type)) onSelect(file);
  };

  return (
    <div>
      <div
        role="button"
        tabIndex={0}
        aria-label="Choose a featured image"
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        className={
          'flex cursor-pointer flex-col items-center gap-2 rounded-xl border-[1.5px] border-dashed ' +
          'px-6 py-8 text-center transition-colors ' +
          (drag ? 'border-lime-700 bg-[#f8fbef]' : 'border-[#c4cda6] bg-[#fcfdf7] hover:border-lime-700 hover:bg-[#f8fbef]')
        }
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="text-lime-700">
          <path d="M12 16V4m0 0L7 9m5-5l5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M5 20h14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
        <span className="font-trirong_reg text-[15px] text-lime-700">Choose a featured image</span>
        <span className="font-trirong_light text-[13px] text-meta">
          PNG or JPG, optional · or drag one here
        </span>
        <input
          ref={inputRef}
          data-testid="featured-input"
          type="file"
          accept="image/png,image/jpeg"
          className="sr-only"
          onChange={onInput}
        />
      </div>

      {value && (
        <div className="mt-4 flex items-start gap-3.5">
          <img
            src={value}
            alt="featured preview"
            className="h-[116px] w-[150px] flex-none rounded-lg object-cover"
          />
          <button
            type="button"
            onClick={onRemove}
            className="font-trirong_reg text-[13px] text-danger hover:underline hover:underline-offset-2"
          >
            Remove
          </button>
        </div>
      )}
    </div>
  );
}
